import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	analyteService,
	AnalyteRequest,
	AnalyteUpdateRequest,
	AnalyteSearchParams,
} from "../services/analyte.service";

// Re-export types for convenience
export type { AnalyteRequest, AnalyteUpdateRequest, AnalyteSearchParams };

export const useAnalytes = () => {
	return useQuery({
		queryKey: ["analytes"],
		queryFn: analyteService.getAnalytes,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};

export const useAnalyteById = (id: string) => {
	return useQuery({
		queryKey: ["analyte", id],
		queryFn: () => analyteService.getAnalyteById(id),
		enabled: !!id,
		staleTime: 5 * 60 * 1000,
	});
};

export const useSearchAnalytes = (params: AnalyteSearchParams) => {
	return useQuery({
		queryKey: ["analytes", "search", params],
		queryFn: () => analyteService.searchAnalytes(params),
		enabled: Object.keys(params).length > 0,
		staleTime: 2 * 60 * 1000, // 2 minutes
	});
};

export const useCreateAnalyte = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: AnalyteRequest) => analyteService.createAnalyte(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["analytes"] });
		},
	});
};

export const useUpdateAnalyte = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: AnalyteUpdateRequest) =>
			analyteService.updateAnalyte(data),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ["analytes"] });
			queryClient.invalidateQueries({ queryKey: ["analyte", data._id] });
		},
	});
};

export const useDeleteAnalyte = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => analyteService.deleteAnalyte(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["analytes"] });
		},
	});
};
